import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconLoading } from '@components/icons/icon-loading';
import { IconSearch } from '@components/icons/icon-search';
import { useGetMagicSearch } from '@hooks/admin/magic-search';
import { useDebouncedValue } from '@hooks/use-debounced-value';
import { type CompanyClubRoute, useOpenCompanyClubRoute } from '@hooks/use-open-company-club-route';
import { isUuid } from '@utils/is-uuid';
import { Command } from 'cmdk';

import { MAGIC_SEARCH_DEBOUNCE_MS } from '../constants';
import { MagicSearchContentProps } from '../magic-search-palette.type';
import { getSearchTerm } from '../utils/get-search-term';

import { MagicSearchCloseButton } from './magic-search-close-button';
import { MagicSearchFooter } from './magic-search-footer';
import { MagicSearchSections } from './magic-search-sections';
import { MagicSearchStatus } from './magic-search-status';

/** Mounted on each opening, so the palette always starts empty. */
export const MagicSearchContent = ({ onClose }: MagicSearchContentProps) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const debouncedValue = useDebouncedValue(inputValue, MAGIC_SEARCH_DEBOUNCE_MS);
  const typedTerm = inputValue.trim();
  const searchableTerm = getSearchTerm(typedTerm);
  // A pasted UUID is the whole term already: nothing is left to type, so there is nothing to wait for.
  const term = isUuid(typedTerm) ? typedTerm : getSearchTerm(debouncedValue);
  const { data, isError, isFetching, isPlaceholderData } = useGetMagicSearch({ term });
  const openCompanyClubRoute = useOpenCompanyClubRoute();

  // Kept from the previous term while the next one loads, never shown for an input with nothing to search.
  const results = searchableTerm !== '' && term !== '' ? data : undefined;
  const resultCount = results ? results.events.length + results.venues.length + results.bookings.length : 0;
  // The results answer the debounced term: an empty answer only means "no results" once that term is what is typed.
  const isAnswerForInput = term === searchableTerm && !isPlaceholderData;
  const isEmpty = !!results && resultCount === 0 && isAnswerForInput;

  const handleOpen = (route: CompanyClubRoute) => {
    onClose();
    openCompanyClubRoute(route);
  };

  return (
    <Command shouldFilter={false} label='Search' className='flex min-h-0 flex-1 flex-col' data-testid='magic-search'>
      <div className='flex h-14 shrink-0 items-center gap-3 border-b border-extra-soft pr-3 pl-4'>
        <IconSearch className='h-5 w-5 shrink-0 fill-current text-tertiary' aria-hidden />
        <Command.Input
          data-autofocus
          value={inputValue}
          onValueChange={setInputValue}
          placeholder={t('menu:magicSearchPlaceholder')}
          className='h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-body-l text-primary placeholder:text-tertiary focus:ring-0 focus:outline-hidden'
          data-testid='magic-search--input'
        />
        {isFetching && searchableTerm !== '' && (
          <IconLoading
            className='h-4 w-4 shrink-0 animate-spin text-tertiary motion-reduce:animate-none'
            aria-hidden
            data-testid='magic-search--fetching'
          />
        )}
        <MagicSearchCloseButton onClose={onClose} />
      </div>
      <Command.List className='min-h-0 flex-1 overflow-y-auto overscroll-contain p-2 scrollbar-xceed'>
        <MagicSearchStatus
          typedTerm={typedTerm}
          isSearchable={searchableTerm !== ''}
          isLoading={!results || (resultCount === 0 && !isEmpty)}
          isError={isError}
          isEmpty={isEmpty}
        />
        {results && !isError && <MagicSearchSections results={results} onOpen={handleOpen} />}
      </Command.List>
      <MagicSearchFooter />
    </Command>
  );
};
